import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {TagRead, TagType} from "../schemas";
import {requiresAssociationConsistency, isTranscriptExchangeTag} from "../schemas/tag";
import {useGroupContext} from "./group_context";
import {getTagsByIds, getTags, getDocumentRequests, getDocumentRequestsByIds} from "../api";
import {INVESTIGATION_COMPLETED, TAG_UPDATE_COMPLETED, TagUpdatedEventDetail} from "../events";
import {withApiErrorHandlerThrow} from "../error_handling";
import {DocumentRequestRead} from "../schemas/document_request";
import {ENABLE_ENTITY_TAGS} from "../feature_flags";
import {TagFilter} from "../schemas/boolean_filtering";
import {EntityPresenceRanges} from "../routines/schemas";


export interface TagProviderContextType {
    tagLoading: boolean;
    setTagLoading: React.Dispatch<React.SetStateAction<boolean>>;

    tagMap: Map<string, TagRead>;
    refreshTags: (tagIds: string[]) => void;
    reFetchTags: () => Promise<{ documentRequests: DocumentRequestRead[], newTagMap: Map<string, TagRead> }>;
    codeTags: TagRead[];
    transcriptTags: TagRead[];
    entityTags: TagRead[];
    documentRequests: DocumentRequestRead[] | undefined;
    setDocumentRequests: React.Dispatch<React.SetStateAction<DocumentRequestRead[] | undefined>>;

    /** Set of tag IDs that should be visible in the grid cells */
    visibleTagIds: Set<string> | null;
    /** Update the set of visible tag IDs (null = show all) */
    setVisibleTagIds: React.Dispatch<React.SetStateAction<Set<string> | null>>;
    /** Toggle visibility for a specific tag ID */
    toggleTagVisibility: (tagId: string) => void;
    /** Set all codes visible or hidden */
    setAllCodesVisible: (visible: boolean) => void;
    /** Set all request tags visible or hidden (optionally filtered by chatId) */
    setAllRequestTagsVisible: (visible: boolean, chatId?: string) => void;

    /** Tag filters for document filtering (used by filter panel) */
    tagFilters: Map<string, TagFilter[]> | null;
    /** Set tag filters */
    setTagFilters: React.Dispatch<React.SetStateAction<Map<string, TagFilter[]> | null>>;
    /** Add an entity tag filter (Include mode by default) */
    addEntityTagFilter: (tagId: string) => void;
    /** Remove a tag filter */
    removeTagFilter: (tagId: string) => void;
    /** Toggle an entity tag filter (add if not present, remove if present) */
    toggleEntityTagFilter: (tagId: string) => void;
}

export const TagProviderContext = createContext<TagProviderContextType | undefined>(undefined);

interface TagProviderProps {
    children: ReactNode;
}

export const TagProvider: React.FC<TagProviderProps> = ({
                                                            children,
                                                        }) => {

    const {
        currentGroup,
    } = useGroupContext()!;

    // Ref tracks current group ID for stable access in event listener closures.
    const currentGroupIdRef = useRef<string | null>(null);
    useEffect(() => {
        currentGroupIdRef.current = currentGroup?.id ?? null;
    }, [currentGroup?.id]);

    const [tagMap, setTagMap] = useState<Map<string, TagRead>>(new Map<string, TagRead>());
    const [codeTags, setCodeTags] = useState<TagRead[]>([]);
    const [transcriptTags, setTranscriptTags] = useState<TagRead[]>([]);
    const [entityTags, setEntityTags] = useState<TagRead[]>([]);
    const [documentRequests, setDocumentRequests] = useState<DocumentRequestRead[] | undefined>(undefined)
    const [tagLoading, setTagLoading] = useState<boolean>(false);

    // Set of tag IDs that should be visible in grid cells (null = show all)
    const [visibleTagIds, setVisibleTagIds] = useState<Set<string> | null>(null);

    // Tag filters for document filtering (shared with filter panel)
    const [tagFilters, setTagFilters] = useState<Map<string, TagFilter[]> | null>(null);

    useEffect(() => {
        const allTags = Array.from(tagMap.values());
        // Issue codes: user-managed TAG tags, excluding AI exchange-type tags
        setCodeTags(allTags.filter(
            tag => requiresAssociationConsistency(tag) && tag.type !== TagType.REQUEST_SET
        ));
        // Transcript exchange tags (Admissions, Denials, etc.)
        setTranscriptTags(allTags.filter(tag => isTranscriptExchangeTag(tag)));
        // Entity tags
        setEntityTags(ENABLE_ENTITY_TAGS
            ? allTags.filter(tag => tag.type === TagType.ENTITY)
            : []
        );
    }, [tagMap]);

    useEffect(() => {
        if (currentGroup) {
            setTagMap(new Map(currentGroup.tags.map(tag => [tag.id, tag])));
            setDocumentRequests(currentGroup.document_requests);
            // Re-fetch tags to get dynamic document_count (the group endpoint
            // returns the stored column value which is 0 for non-folder tags)
            reFetchTags().catch(() => {});
        }
    }, [currentGroup?.id]);


    useEffect(() => {
        const onTagUpdated = async (event: Event) => {
            const customEvent = event as CustomEvent<TagUpdatedEventDetail>;
            console.log('tag_context TAG_UPDATE_COMPLETED', customEvent);
            await reFetchTags();
        };

        const onInvestigationCompleted = async (event: Event) => {
            console.log('tag_context INVESTIGATION_COMPLETED', event);
            // Investigation creates new tags, so re-fetch all tags
            await reFetchTags();
        };

        window.addEventListener(TAG_UPDATE_COMPLETED, onTagUpdated);
        window.addEventListener(INVESTIGATION_COMPLETED, onInvestigationCompleted);
        return () => {
            window.removeEventListener(TAG_UPDATE_COMPLETED, onTagUpdated);
            window.removeEventListener(INVESTIGATION_COMPLETED, onInvestigationCompleted);
        }
    }, [])

    const refreshTags = async (tagIds: string[]): Promise<void> => {
        const tagsToUpdate: TagRead[] = await withApiErrorHandlerThrow(
            () => getTagsByIds(tagIds, currentGroup?.id),
            {titleQualifier: "Error Fetching Tags"}
        );

        setTagMap(prevTags => {
            const newTagMap = new Map(prevTags); // new reference
            for (const newTag of tagsToUpdate) {
                newTagMap.set(newTag.id, newTag); // overwrite or insert
            }
            return newTagMap;
        });

        const docReqIds: string[] = Array.from(
            new Set(
                tagsToUpdate
                    .map(tag => tag.document_request_id)
                    .filter((id): id is string => Boolean(id))
            )
        );
        if (!docReqIds.length) return;
        const newDocumentRequests: DocumentRequestRead[] = await withApiErrorHandlerThrow(
            () => getDocumentRequestsByIds(docReqIds),
            {titleQualifier: "Error Fetching Document Requests"});
        setDocumentRequests(prevDocReqs => {
            const docReqMap = new Map(prevDocReqs?.map(dr => [dr.id, dr])); // new reference
            for (const newDocReq of newDocumentRequests) {
                docReqMap.set(newDocReq.id, newDocReq); // overwrite or insert
            }
            return Array.from(docReqMap.values());
        });
    };

    const reFetchTags = async (): Promise<{ documentRequests: DocumentRequestRead[], newTagMap: Map<string, TagRead> }> => {
        const groupId = currentGroupIdRef.current;
        if (!groupId) {
            return {documentRequests: documentRequests ?? [], newTagMap: tagMap};
        }
        const newTags: TagRead[] = await withApiErrorHandlerThrow(
            () => getTags(groupId),
            {titleQualifier: "Error Fetching Tags"});
        const newTagMap = new Map(newTags.map(tag => [tag.id, tag]));
        setTagMap(newTagMap);

        // Auto-add new tags to visibleTagIds so they appear in the grid
        setVisibleTagIds(prev => {
            if (prev === null) return null; // show-all mode, nothing to do
            const next = new Set(prev);
            for (const tag of newTags) {
                if (!prev.has(tag.id)) next.add(tag.id);
            }
            return next;
        });

        const newDocumentRequests: DocumentRequestRead[] = await withApiErrorHandlerThrow(
            () => getDocumentRequests(),
            {titleQualifier: "Error Fetching Document Requests"});
        setDocumentRequests(newDocumentRequests);
        return {documentRequests: newDocumentRequests, newTagMap: newTagMap};
    }

    // Toggle visibility for a specific tag ID
    const toggleTagVisibility = useCallback((tagId: string) => {
        setVisibleTagIds((prev) => {
            if (prev === null) {
                // Currently showing all - create a set with all except this one
                const allIds = new Set(Array.from(tagMap.keys()));
                allIds.delete(tagId);
                return allIds;
            }
            const next = new Set(prev);
            if (next.has(tagId)) {
                next.delete(tagId);
            } else {
                next.add(tagId);
            }
            // If set is empty, keep it as empty (user wants to hide all)
            return next;
        });
    }, [tagMap]);

    // Set all codes visible or hidden
    const setAllCodesVisible = useCallback((visible: boolean) => {
        setVisibleTagIds((prev) => {
            const codeIds = Array.from(tagMap.values())
                .filter(tag => tag.type === TagType.TAG)
                .map(tag => tag.id);

            if (visible) {
                // Add all code IDs to visible set
                const next = new Set(prev ?? []);
                codeIds.forEach(id => next.add(id));
                return next.size > 0 ? next : null;
            } else {
                // Remove all code IDs from visible set
                if (prev === null) {
                    // Currently showing all - create set with only non-code tags
                    const next = new Set<string>();
                    for (const tag of tagMap.values()) {
                        if (tag.type !== TagType.TAG) {
                            next.add(tag.id);
                        }
                    }
                    return next.size > 0 ? next : new Set();
                }
                const next = new Set(prev);
                codeIds.forEach(id => next.delete(id));
                return next;
            }
        });
    }, [tagMap]);

    // Set all request tags visible or hidden (optionally filtered by chatId)
    const setAllRequestTagsVisible = useCallback((visible: boolean, chatId?: string) => {
        setVisibleTagIds((prev) => {
            const requestTags = Array.from(tagMap.values())
                .filter(tag => {
                    if (tag.type !== TagType.REQUEST_SET) return false;
                    if (chatId && tag.chat_id !== chatId) return false;
                    return true;
                })
                .map(tag => tag.id);

            if (visible) {
                // Add all matching request tag IDs to visible set
                const next = new Set(prev ?? []);
                requestTags.forEach(id => next.add(id));
                return next.size > 0 ? next : null;
            } else {
                // Remove all matching request tag IDs from visible set
                if (prev === null) {
                    // Currently showing all - create set without these tags
                    const next = new Set(Array.from(tagMap.keys()));
                    requestTags.forEach(id => next.delete(id));
                    return next;
                }
                const next = new Set(prev);
                requestTags.forEach(id => next.delete(id));
                return next;
            }
        });
    }, [tagMap]);

    // Add an entity tag filter with Include mode
    const addEntityTagFilter = useCallback((tagId: string) => {
        const { min, max } = EntityPresenceRanges['Include'];
        setTagFilters((prev) => {
            const next = new Map(prev ?? []);
            next.set(tagId, [{ tag_id: tagId, min_score: min, max_score: max }]);
            return next;
        });
    }, []);

    // Remove a tag filter
    const removeTagFilter = useCallback((tagId: string) => {
        setTagFilters((prev) => {
            if (!prev) return null;
            const next = new Map(prev);
            next.delete(tagId);
            return next.size > 0 ? next : null;
        });
    }, []);

    // Toggle an entity tag filter (add if not present, remove if present)
    const toggleEntityTagFilter = useCallback((tagId: string) => {
        setTagFilters((prev) => {
            const next = new Map(prev ?? []);
            if (next.has(tagId)) {
                next.delete(tagId);
                return next.size > 0 ? next : null;
            } else {
                const { min, max } = EntityPresenceRanges['Include'];
                next.set(tagId, [{ tag_id: tagId, min_score: min, max_score: max }]);
                return next;
            }
        });
    }, [tagMap]);

    const contextValue: TagProviderContextType = useMemo(() => ({
        tagLoading,
        setTagLoading,
        tagMap,
        codeTags,
        transcriptTags,
        entityTags,
        refreshTags,
        reFetchTags,
        documentRequests,
        setDocumentRequests,
        visibleTagIds,
        setVisibleTagIds,
        toggleTagVisibility,
        setAllCodesVisible,
        setAllRequestTagsVisible,
        tagFilters,
        setTagFilters,
        addEntityTagFilter,
        removeTagFilter,
        toggleEntityTagFilter,
    }), [
        tagLoading, tagMap, codeTags, transcriptTags, entityTags, documentRequests,
        visibleTagIds, tagFilters,
        refreshTags, reFetchTags, toggleTagVisibility,
        setAllCodesVisible, setAllRequestTagsVisible,
        addEntityTagFilter, removeTagFilter, toggleEntityTagFilter,
    ]);

    return (
        <TagProviderContext.Provider value={contextValue}>
            {children}
        </TagProviderContext.Provider>
    );
};

export const useTagContext = () => useContext(TagProviderContext);
